<script setup>
import { Card, Heading, Button, Input } from '@components'
import { defineProps, useSlots, computed } from 'vue';
const props = defineProps({
    dark: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'md',
    },
    label: {
        type: String,
        default: 'Preview',
    },
    color: {
        type: [Object, Boolean],
        default: false,
    },
})

const slots = useSlots()
const isDefaultSlot = slots.default && slots.default().length > 0

const color = computed(() => {
    if ( props.color && typeof props.color === 'object' ) {
        return props.color
    }

    return {
        text: props.dark ? '#F0F0F0' : '#111827',
        bg: props.dark ? '#222' : '#F0F0F0',
        link: props.dark ? '#F59E0B' : '#EF4444',
        input_text: props.dark ? '#F0F0F0' : '#111827',
        input_bg: props.dark ? '#222' : '#F0F0F0',
        input_border: props.dark ? '#F0F0F0' : '#111827',
        button_bg: props.dark ? '#F59E0B' : '#EF4444',
    }
})

const wrapperClasses = computed( () => {
    let classes = []

    // Size
    const sizes = {
        'md' : 'w-80',
        'lg' : 'w-96',
        'sm' : 'w-64',
    }

    classes.push( sizes[props.size || 'sm'] )

    return classes.join(' ')
})

</script>

<template>
    <!-- preview section  -->
    <Card transparent borderless class="w-fit">
        <Heading v-if="props.label" class="text-center">{{ props.label }}</Heading>
        <div dark class="relative transition duration-75 border rounded-xl" :class="{
            'w-80' : props.size === 'md',
            'w-96' : props.size === 'lg',
            'w-64' : props.size === 'sm',
        }" :style="{background: color.bg, color: color.text}">
            <div class="flex items-center w-full h-10 px-4 transition duration-75 rounded-t-xl" :style="{background: color.bg}">
                <svg class="w-7" viewBox="0 0 30 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="4" cy="4" r="4" fill="#EF4444" />
                    <circle cx="15" cy="4" r="4" fill="#F59E0B" />
                    <circle cx="26" cy="4" r="4" fill="#10B981" />
                </svg>
            </div>
            
            <slot v-if="isDefaultSlot" /> 

            <div v-else class="flex flex-col gap-5 p-5 font-sans default-slot break-words"> 
                <div class="flex items-center justify-between rounded-lg wp-dark-mode-link flex-wrap" :style="`--wpdm-link: ${color.link}; --wpdm-link-hover: ${color.link_hover};`">
                    <a class="py-3 text-base leading-none" :style="{color: color.link}" href="javascript:;" v-for="a in ['Home', 'Features', 'Pricing', 'Contact']" :key="a">{{ a }}</a>
                </div>
                <div class="text-3xl font-medium leading-none" :style="{color: color.text}">Doing it all.
                    <br />In all new ways</div>
                <div class="text-sm leading-none  break-words">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed ultricies ex at odio porttitor, vitae commodo erat pulvinar.</div>
                <div class="max-w-full">
                    <input class="leading-none w-fit max-w-full wp-dark-mode-input px-4 py-2 rounded break-words" placeholder="Enter your email" :style="`--wpdm-input-bg: ${color.input_bg}; --wpdm-input-text: ${color.input_text}; --wpdm-input-border: ${color.input_border}; --wpdm-input-placeholder: ${color.input_placeholder};`" />
                </div>
                <div>
                    <button class="leading-none hover:opacity-90 transition duration-100 px-6 py-2 rounded wp-dark-mode-button" size="xl" :style="`--wpdm-button-bg: ${color.button_bg}; --wpdm-button-text: ${color.button_text}; --wpdm-button-border: ${color.button_border}; --wpdm-button-hover-bg: ${color.button_hover_bg}; --wpdm-button-hover-text: ${color.button_hover_text};`">Explore now</button>
                </div>
            </div>
         
        </div>
    </Card>
</template>

<style>

.wp-dark-mode-link a {
    color: var(--wpdm-link) !important;
}

.wp-dark-mode-link a:hover {
    color: var(--wpdm-link-hover) !important;
}

.wp-dark-mode-input {
    background: var(--wpdm-input-bg) !important;
    color: var(--wpdm-input-text) !important;
    border: 2px solid var(--wpdm-input-border) !important;
}

.wp-dark-mode-input::placeholder {
    color: var(--wpdm-input-placeholder) !important;
}

.wp-dark-mode-button {
    background: var(--wpdm-button-bg) !important;
    color: var(--wpdm-button-text) !important;
    border: 2px solid var(--wpdm-button-border) !important;
}

.wp-dark-mode-button:hover {
    background: var(--wpdm-button-hover-bg) !important;
    color: var(--wpdm-button-hover-text) !important;
}

</style>